import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Overline } from "./Mark";
import { Reveal } from "./Reveal";

const PRINCIPLES = [
  { k: "Reliable", v: "communication with every prospect and partner." },
  { k: "Transparent", v: "business practices, described accurately." },
  { k: "Professional", v: "experience for every single enquiry." },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} className="bg-[#070E17] py-24 lg:py-36" data-testid="about-section">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>About Us</Overline>
            <h2 className="font-serif-d mt-6 text-4xl font-medium leading-[1.05] text-white sm:text-5xl">Helping property professionals reach <em className="gold-text italic">interested audiences.</em></h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <div className="relative aspect-[4/5] overflow-hidden rounded-[28px]">
              <motion.img style={{ y }} src="/img/hero.jpg" alt="Elegant residential living space" className="absolute inset-0 h-[116%] w-full -translate-y-[8%] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070E17]/60 to-transparent" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 lg:pt-24">
          <Reveal>
            <p className="font-serif-d text-2xl leading-snug text-white sm:text-3xl" data-testid="about-lead">
              VK Enterprises is a property consultancy and real-estate marketing business focused on helping property professionals reach interested audiences.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-slate-300">Through digital advertising, website enquiries and professional follow-up, we support property marketing activities across selected international markets.</p>
            <p className="mt-4 text-base leading-relaxed text-slate-300">Our focus is on professional communication, effective lead generation and building meaningful connections between interested prospects and real-estate professionals.</p>
          </Reveal>
          <div className="mt-14">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.k} delay={i * 0.08}>
                <div className="group flex items-baseline gap-6 border-t border-[rgba(212,175,55,0.18)] py-6" data-testid={`about-principle-${i + 1}`}>
                  <span className="font-mono-d text-xs text-[#D4AF37]">0{i + 1}</span>
                  <p className="text-lg text-slate-300"><span className="font-serif-d mr-2 text-3xl text-white transition-colors duration-500 group-hover:text-[#E6C280]">{p.k}</span>{p.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-12">
            <div className="flex flex-col items-center gap-6 rounded-[28px] bg-white p-6 sm:flex-row sm:p-8" data-testid="about-brand-card">
              <img src="/img/logo.jpg" alt="VK Enterprises logo" className="h-36 w-36 shrink-0 object-contain" />
              <p className="text-center text-sm leading-relaxed text-[#0B192C] sm:text-left">Our goal is to provide reliable communication, transparent business practices and a professional experience for every enquiry.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
